import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { format as formatDate, parseISO as parseISODate } from 'date-fns';
import { useMediaQuery } from '../../utils/hooks/useMediaQuery';
import { breakpointQueries, breakpoints, colors, spacing } from '../../utils/styleguide';
import Icon from './Icon';
import Image from './Image';
import Typography from './text/Typography';

const Wrapper = styled.a`
  display: flex;
  flex-direction: column-reverse;
  border-radius: 2px;
  border: 1px ${colors.grey03} solid;
  text-decoration: none;

  &:hover {
    cursor: pointer;
    background-color: ${colors.grey01};
  }

  ${breakpoints.desktop} {
    flex-direction: row;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  padding: ${spacing[4]}px ${spacing[2]}px;

  ${breakpoints.tablet} {
    padding: ${spacing[5]}px;
  }

  ${breakpoints.desktop} {
    height: unset;
    flex: 1;
    padding: ${spacing[5]}px ${spacing[4]}px;
    padding-right: ${spacing[6]}px;
  }
`;

const Row = styled.div<{ gap?: number }>`
  display: flex;
  gap: ${spacing[1]}px;
  flex-wrap: wrap;
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 250px;

  ${breakpoints.desktop} {
    height: unset;
    width: 40%;
  }
`;

const imageStyle = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

type Item = {
  item: {
    type: 'Article' | 'Webinar';
    title?: string;
    description?: string;
    publishedAt: string;
    duration?: number;
    url: string;
    externalLink?: string;
    image?: {
      url: string;
      alt: string;
      width: number;
      height: number;
    };
    profiles: string;
  };
};

export default function ListItemCard({ item }: Item) {
  const [isTabletOrLarger] = useMediaQuery(breakpointQueries.tablet);

  const imageUrl = item.image?.url;

  return (
    <Wrapper
      href={item.externalLink ? item.externalLink : item.url}
      target={item.externalLink ? '_blank' : '_self'}
    >
      <ContentWrapper>
        <Row>
          <Typography variant="detail" color={colors.blackSecondary}>
            {item.profiles}
          </Typography>
          {item.profiles && (
            <Typography variant="detail" color={colors.blackSecondary}>
              ·
            </Typography>
          )}
          <Typography variant="detail" color={colors.blackSecondary}>
            {formatDate(parseISODate(item.publishedAt || ''), 'MMM dd, yyyy')}
          </Typography>
        </Row>
        <Typography variant={isTabletOrLarger ? 'h4' : 'h5Bold'}>{item.title}</Typography>
        <Typography maxNumberOfLines={2} color={colors.grey04}>
          {item.description}
        </Typography>
        {!!item.duration && (
          <Row>
            <Icon name="clock" stroke={colors.blackSecondary} />
            <Typography variant="detail" color={colors.blackSecondary}>
              {`${item.duration} min${item.type === 'Article' ? ' read' : ''}`}
            </Typography>
          </Row>
        )}
      </ContentWrapper>
      {imageUrl && (
        <ImageWrapper>
          <Image css={imageStyle} url={imageUrl} alt={item.image?.alt ?? ''} />
        </ImageWrapper>
      )}
    </Wrapper>
  );
}
